import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FilterAttributeRange } from '@df/catalog-ui/listing/filter/filter-attribute-range';
import { FilterAttributeRangeComponentDef } from '@df/catalog-ui/listing/filter/filter-attribute-range.component.def';
import { Model } from '@df/catalog/model';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { HideUpComponent } from '@df/ui/hide-up/hide-up.component';
import { PriceComponent } from '@df/ui/price/price.component';
import { ToggleModule } from '@df/ui/toggle/toggle.module';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { ucfirst } from '@df/util/fn/ucfirst.fn';
import { Subscription } from 'rxjs/internal/Subscription';

class FilterRangeEntry {
    value?: number;
    private filterSubscription: Subscription;

    constructor(
        private readonly type: 'min' | 'max',
        private readonly filter: FilterAttributeRange<Model>
    ) {
        this.sync();
        this.filterSubscription = this.filter?.subscribe(() => this.sync());
    }

    private sync() {
        this.value = this.filter[this.type];
    }

    apply(): void {
        if (this.type === 'min') {
            this.filter.min = typeof this.filter.max === 'number' ? Math.min(this.filter.max, this.value) : this.value;
        } else {
            this.filter.max = typeof this.filter.min === 'number' ? Math.max(this.filter.min, this.value) : this.value;
        }
    }

    clear(): void {
        this.filter[this.type] = undefined;
    }

    canClear(): boolean {
        return typeof this.filter[this.type] === 'number';
    }

    destroy(): void {
        this.filterSubscription?.unsubscribe();
    }

    get label() {
        return ucfirst(this.type);
    }
}

@Component({
    selector: 'filter-attribute-range',
    templateUrl: './filter-attribute-range.html',
    styleUrl: './filter-attribute-range.scss',
    standalone: true,
    imports: [CommonModule, FormsModule, GridModule, HideUpComponent, PriceComponent, ToggleModule, TranslatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilterAttributeRangeComponent<T extends Model> extends FilterAttributeRangeComponentDef<T> {
    filterMin?: FilterRangeEntry;
    filterMax?: FilterRangeEntry;

    readonly filterMinOption = {};

    override ngOnChanges(changes: SimpleChanges) {
        if (changes['filter']) {
            this.filterMin?.destroy();
            this.filterMax?.destroy();

            if (this.filter) {
                this.filterMin = new FilterRangeEntry('min', this.filter);
                this.filterMax = new FilterRangeEntry('max', this.filter);
            } else {
                this.filterMin = this.filterMax = undefined;
            }
        }

        super.ngOnChanges(changes);
    }

    override ngOnDestroy() {
        this.filterMin?.destroy();
        this.filterMax?.destroy();

        super.ngOnDestroy();
    }
}
